import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Youtube, ExternalLink, ChevronLeft, ChevronRight } from 'lucide-react';
import { useEffect, useRef, useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';

const CHANNEL_URL = 'https://www.youtube.com/@ConexusGlobalConsulting';

type Video = { id: string; titleKey?: string; title?: string };

const RAW_VIDEOS: Video[] = [
  { id: 'uQznmVko-Ek', titleKey: 'youtube.video1Title' },
  { id: '4tNmZIl9xVk', titleKey: 'youtube.video2Title' },
  { id: 'j5UF1Ug84d4', titleKey: 'youtube.video3Title' },
  { id: 'XzOzwyCqgOs', title: 'A Conexus faz tudo diferente! 🙌🏻' },
  { id: '3ZXfYDz2Pq8', title: 'Conexus introduces the new Bachelor of Business Administration – AI Concentration at BUA (Part I)' },
  { id: '4qLZ2HN_9bs', title: 'Conexus introduces the Bachelor of Business Administration – AI Concentration at BUA (Part II)' },
];

// Deduplicate by video id (preserves first occurrence order)
const VIDEOS: Video[] = Array.from(
  new Map(RAW_VIDEOS.map((v) => [v.id, v])).values()
);

const YouTubeSection = () => {
  const { t } = useTranslation();
  const [activeIndex, setActiveIndex] = useState(0);
  const [direction, setDirection] = useState(0);

  const goTo = (newIndex: number) => {
    setDirection(newIndex > activeIndex ? 1 : -1);
    setActiveIndex(newIndex);
  };

  const prev = () => goTo((activeIndex - 1 + VIDEOS.length) % VIDEOS.length);
  const next = () => goTo((activeIndex + 1) % VIDEOS.length);

  const activeVideo = VIDEOS[activeIndex];
  const activeTitle = activeVideo.titleKey ? t(activeVideo.titleKey) : (activeVideo.title ?? '');

  const variants = {
    enter: (dir: number) => ({ x: dir > 0 ? 300 : -300, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (dir: number) => ({ x: dir > 0 ? -300 : 300, opacity: 0 }),
  };

  return (
    <section className="py-24 bg-background">
      <div className="container mx-auto px-4">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-4">
            <Youtube className="h-8 w-8 text-destructive shrink-0" />
            <h2 className="text-3xl md:text-4xl font-bold text-foreground text-center">
              {t('youtube.title')}
            </h2>
          </div>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-6">
            {t('youtube.subtitle')}
          </p>
          <a
            href={CHANNEL_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-destructive font-semibold hover:underline"
          >
            {t('youtube.channelName')}
            <ExternalLink className="h-4 w-4" />
          </a>
        </motion.div>

        {/* Featured Video Carousel */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="relative max-w-4xl mx-auto"
        >
          {/* Navigation Arrows */}
          <Button
            variant="outline"
            size="icon"
            onClick={prev}
            className="absolute -left-5 md:-left-14 top-[calc(50%-40px)] z-20 h-11 w-11 rounded-full bg-background/90 backdrop-blur border-border shadow-lg hover:bg-accent"
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={next}
            className="absolute -right-5 md:-right-14 top-[calc(50%-40px)] z-20 h-11 w-11 rounded-full bg-background/90 backdrop-blur border-border shadow-lg hover:bg-accent"
          >
            <ChevronRight className="h-5 w-5" />
          </Button>

          {/* Video display */}
          <div className="overflow-hidden rounded-2xl shadow-xl border border-border/50">
            <AnimatePresence mode="wait" custom={direction}>
              <motion.div
                key={activeVideo.id}
                custom={direction}
                variants={variants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.35, ease: 'easeInOut' }}
              >
                <div className="aspect-video">
                  <iframe
                    src={`https://www.youtube-nocookie.com/embed/${activeVideo.id}?rel=0&modestbranding=1&playsinline=1`}
                    title={activeTitle}
                    allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="w-full h-full"
                  />
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Video title */}
          <AnimatePresence mode="wait">
            <motion.p
              key={activeVideo.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.25 }}
              className="text-center mt-4 text-lg font-semibold text-foreground"
            >
              {activeTitle}
            </motion.p>
          </AnimatePresence>

          {/* Dot indicators */}
          <div className="flex justify-center gap-3 mt-6">
            {VIDEOS.map((_, i) => (
              <button
                key={i}
                onClick={() => goTo(i)}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${
                  i === activeIndex
                    ? 'bg-destructive scale-125'
                    : 'bg-border hover:bg-muted-foreground'
                }`}
                aria-label={`Go to video ${i + 1}`}
              />
            ))}
          </div>
        </motion.div>

        {/* Subscribe CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-center mt-10"
        >
          <a
            href={CHANNEL_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-destructive text-destructive-foreground px-8 py-3 rounded-full font-semibold hover:opacity-90 transition-opacity shadow-lg"
          >
            <Youtube className="h-5 w-5" />
            {t('youtube.subscribeButton')}
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default YouTubeSection;
